import { DialogActions, DialogContent } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { CarrierUnitDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { parseErrorMessage } from '../../../../../../core/api/api.context';
import { Modal } from '../../../../../../ui/components/pure/modal/modal.component';
import { Snackbar } from '../../../../../../ui/components/pure/snackbar/snackbar.component';
import { dialogActionsStyle } from '../../../../components/courier-form.styles';
import {
  BatchImportCouriersActionHandlers,
  BatchImportCouriersActionState
} from '../batch-import-couriers-action.hook';
import { worstSeverity } from '../../../../logic/courier-batch.utils';
import { CourierBatchActionsBar } from './courier-batch-actions-bar.component';
import { CourierBatchGrid } from './courier-batch-grid.component';

const alertStyle = { marginBottom: '12px' };

export function CourierBatchReviewModal({
  state,
  handlers,
  units
}: {
  state: BatchImportCouriersActionState;
  handlers: BatchImportCouriersActionHandlers;
  units: CarrierUnitDto[];
}) {
  return (
    <Modal title="Batch import" open={true} maxWidth="xl" pinFooter>
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        {state.error && (
          <Alert variant="outlined" severity="error" style={alertStyle}>
            <AlertTitle>Something went wrong</AlertTitle>
            {parseErrorMessage(state.error.message)}
          </Alert>
        )}
        {state.batchDiagnostics.map((diagnostic, index) => (
          <Alert key={index} variant="outlined" severity={worstSeverity([diagnostic])} style={alertStyle}>
            {diagnostic.message}
          </Alert>
        ))}
        {state.clearedCarrierCount > 0 && (
          <Alert variant="outlined" severity="info" style={alertStyle}>
            <AlertTitle>
              {state.clearedCarrierCount === 1
                ? '1 courier named a carrier that could not be found'
                : `${state.clearedCarrierCount} couriers named a carrier that could not be found`}
            </AlertTitle>
            The carrier was cleared from each one, and they&apos;re shown with errors. Pick a carrier for each, or
            validate again to use the default carrier.
          </Alert>
        )}
        {state.unmapped.length > 0 && (
          <Alert variant="outlined" severity="warning" style={alertStyle}>
            <AlertTitle>Some columns were ignored</AlertTitle>
            These headings don't match a courier field: {state.unmapped.join(', ')}.
          </Alert>
        )}

        <CourierBatchGrid
          rows={state.rows}
          units={units}
          handlers={handlers}
          isDisabled={state.isPreparing || state.isUploading}
          validatingRowIDs={state.validatingRowIDs}
        />
      </DialogContent>
      <DialogActions style={dialogActionsStyle}>
        <CourierBatchActionsBar state={state} handlers={handlers} />
      </DialogActions>

      {/* Inside the dialog, so the modal doesn't hide it from screen readers. */}
      {state.isToastOpen && (
        <Snackbar
          severity="warning"
          onClose={handlers.onToastClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          Couriers with errors won't be uploaded. Fix them and validate again, or upload only the valid couriers.
        </Snackbar>
      )}
    </Modal>
  );
}
